/**
 * 「有新版本」通知：牌桌右上角、顶栏下面的一枚小胶囊，带「立即更新」与关闭。
 *
 * ★ 为什么不放在原来那条 Toast 的位置（底部）：
 *   Toast 是 4 秒就走的状态播报，所以整条 pointer-events:none（它曾经悬在等待室按钮行上，
 *   把「加 AI 补位」的第一下点击吞掉）。这条通知要一直挂到更新完成、而且带按钮 ——
 *   放在底部就会长期压住「催一下」和预选胶囊。右上角顶栏下面是牌桌上唯一不压操作的地方。
 *   容器本身仍然不吃点击，只有两颗按钮可点。
 *
 * ★ 「立即更新」是**用户主动**的刷新（红线 #57 管的是自动触发）：
 *   联机牌局中刷新，服务端会保住座位、重连后接上这一手；本地牌局没有存档，要再点一次确认。
 */
import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'

import { IconClose } from '../icons/index.js'
import { useLocalSessionStore } from '../local/localSession.js'
import { useUpdateStore } from '../pwa/updateStore.js'
import { updateHint } from '../pwa/updateIdle.js'
import { isTableIdle, useTableStore, useUiStore } from '../store/index.js'
import './session.css'

/** 本地牌局的二次确认窗口 */
const ARM_MS = 3000

/**
 * 点「立即更新」这一下该做什么。
 * 本地牌局没有存档：第一下只是「上膛」，3 秒内再点才真的刷新；更新中再点什么都不做。
 */
export function updateTapStep(localGame: boolean, armed: boolean, updating: boolean): 'arm' | 'apply' | 'none' {
  if (updating) return 'none'
  if (localGame && !armed) return 'arm'
  return 'apply'
}

export interface UpdateNoticeProps {
  /** 覆盖 store 里的值（单测用；省略就订阅 store） */
  readonly waiting?: boolean
  readonly dismissed?: boolean
  readonly view?: 'lobby' | 'table'
  readonly localGame?: boolean
  readonly tableIdle?: boolean
  /** 断线条在的时候让位（两者都挂在顶栏下面） */
  readonly online?: boolean
}

export function UpdateNotice(over: UpdateNoticeProps = {}): ReactNode {
  const storeWaiting = useUpdateStore((s) => s.waiting)
  const storeDismissed = useUpdateStore((s) => s.dismissed)
  const apply = useUpdateStore((s) => s.apply)
  const dismiss = useUpdateStore((s) => s.dismiss)
  const storeView = useUiStore((s) => s.view)
  const storeLocal = useLocalSessionStore((s) => s.status !== 'idle')
  const storeIdle = useTableStore((s) => isTableIdle(s.view))
  const connection = useTableStore((s) => s.connection)

  const waiting = over.waiting ?? storeWaiting
  const dismissed = over.dismissed ?? storeDismissed
  const view = over.view ?? storeView
  const localGame = over.localGame ?? storeLocal
  const tableIdle = over.tableIdle ?? storeIdle
  const online = over.online ?? (localGame || view === 'lobby' || connection === 'live' || connection === 'idle')

  const [armed, setArmed] = useState(false)
  const [updating, setUpdating] = useState(false)

  useEffect(() => {
    if (!armed) return undefined
    const t = setTimeout(() => setArmed(false), ARM_MS)
    return () => clearTimeout(t)
  }, [armed])

  if (!waiting || dismissed || !online) return null

  const onUpdate = (): void => {
    const step = updateTapStep(localGame, armed, updating)
    if (step === 'arm') setArmed(true)
    if (step !== 'apply') return
    setUpdating(true)
    apply?.()
  }

  const label = updating ? '更新中…' : armed ? '会结束本地牌局，再点一次' : '立即更新'
  const hint = updateHint({ view, localGame, tableIdle })
  // 牌桌上只留「新版本 + 两颗按钮」：7–8 人桌的顶上两个座位离舞台上沿只有十几二十像素，
  // 整句说明会压住右上那位的头像。说明挪进读屏标签；大厅里地方宽，照旧显示
  const compact = view === 'table'

  return (
    <div
      className="update-notice"
      role="status"
      aria-label={`有新版本，${hint}`}
      data-armed={armed ? 'on' : 'off'}
      data-compact={compact ? 'on' : 'off'}
    >
      <span className="update-notice__text">
        <b>新版本</b>
        {!compact && <span className="update-notice__hint">{hint}</span>}
      </span>
      <button type="button" className="update-notice__go" disabled={updating} onClick={onUpdate}>
        {label}
      </button>
      <button type="button" className="update-notice__close" aria-label="先不更新" onClick={dismiss}>
        <IconClose size={14} />
      </button>
    </div>
  )
}

export default UpdateNotice
